import {
  AlertCircle,
  FileText,
  Image as ImageIcon,
  ListChecks,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from "@/components/ui/item";
import { CollectionTableRow } from "@/components/reports/collection-table-row/collection-table-row";
import { ReportProgressBar } from "@/components/reports/report-item/report-progress-bar";
import { useReportProgress } from "@/components/reports/report-item/use-report-progress";
import { cn } from "@/lib/utils";
import type {
  CollectionExport,
  ProjectUser,
  ReportCollection,
} from "@/types/api";
import { formatCollectionDate } from "./format-collection-date";

interface CollectionRowProps {
  projectId: string;
  collection: ReportCollection;
  /**
   * The shape to draw: the card, or on a desktop-sized window a row of columns
   * ([[collection-table-row.spec]]). The list chooses, because the table needs
   * a frame and a header a row cannot draw. Defaults to the card.
   */
  layout?: "card" | "table";
}

function creatorName(user: ProjectUser | null): string {
  if (!user) return "Unknown user";
  return `${user.first_name} ${user.last_name}`.trim() || "Unknown user";
}

/**
 * One row on the Reports page. Tapping it opens the report, not the file —
 * re-exporting is the point of the feature, so the page wins the tap. The
 * file stays one tap away inside the report. See [[collection-row.spec]].
 *
 * Keyed on the latest export because the progress simulation belongs to the
 * *export*, not to the collection: `useReportProgress` holds its start time in
 * a ref (and in localStorage), so a re-export has to begin on a fresh hook or
 * it resumes the finished one's curve and opens at 90%.
 */
export function CollectionRow({
  projectId,
  collection,
  layout = "card",
}: CollectionRowProps) {
  return (
    <CollectionRowBody
      key={collection.latest_export?.id ?? "no-export"}
      projectId={projectId}
      collection={collection}
      layout={layout}
    />
  );
}

function CollectionRowBody({
  projectId,
  collection,
  layout = "card",
}: CollectionRowProps) {
  const latest = collection.latest_export;
  const isPhotos = collection.kind === "records";
  const count = isPhotos ? (collection.photo_count ?? 0) : collection.item_count;

  // The simulation drives the in-progress UI and tells us when to hand over to
  // the completed layout (see report-item.spec "Completing" state). Safe to
  // mount unconditionally — it is a no-op for terminal statuses. The count
  // calibrates the estimate: photos for a records report, entries for an
  // issues one; 0 (an unknown photo count) falls back to the hook's default.
  const progress = useReportProgress(
    latest?.id ?? collection.id,
    latest?.status ?? "COMPLETED",
    count,
    collection.kind,
  );

  const statusInProgress =
    latest?.status === "PENDING" || latest?.status === "PROCESSING";
  // Hold the in-progress visual until the bar settles (`isDone`) so the
  // completion handoff is one smooth motion. Only flips early on FAILED.
  const isInProgress =
    statusInProgress || (latest?.status === "COMPLETED" && !progress.isDone);
  const isFailed = latest?.status === "FAILED";
  const isCompleted = !isInProgress && !isFailed;

  // The same state, drawn in columns. The progress hook above is shared, so a
  // row that switches shape mid-export keeps its curve.
  if (layout === "table") {
    return (
      <CollectionTableRow
        projectId={projectId}
        collection={collection}
        count={count}
        isInProgress={isInProgress}
        isFailed={isFailed}
        progress={progress}
      />
    );
  }

  const body = (
    <>
      <CollectionThumbnail
        latest={latest}
        isInProgress={isInProgress}
        isFailed={isFailed}
      />
      <CollectionContent
        collection={collection}
        latest={latest}
        isPhotos={isPhotos}
        count={count}
        isInProgress={isInProgress}
        isFailed={isFailed}
        progress={progress}
      />
    </>
  );

  return (
    <Item
      variant="outline"
      className={cn(
        // An outline item has no fill, so the row adds the card fill itself:
        // white in light mode and the dark card in dark, the same as the Photos
        // and Issues lists. See [[theming]] § One card fill.
        "w-full p-0 overflow-hidden bg-card",
        isFailed && "border-l-2 border-destructive",
        // Lifts on hover and press, in dark mode, but only when the row opens
        // something: an exporting or failed row is not a link, and a hover on
        // it would promise a tap that does nothing. See [[theming]] § Card rows
        // lift on hover and press.
        isCompleted &&
          "dark:hover:border-border-strong dark:hover:bg-card-hover dark:active:bg-card-pressed",
      )}
    >
      <div
        data-slot="collection-row"
        className={cn(
          // min-h, not h: prod's row is 96px and this one reads as 96px for
          // every ordinary report, but prod's title was bare text and prod had
          // no removed-items line. A FIXED 96px here makes the content box
          // (96 − padding) smaller than its contents, and because every line in
          // it clips its own overflow (truncate / line-clamp) flexbox is
          // allowed to shrink them to nothing rather than overflow — measured
          // in Chrome at 375px: the two description lines squashed 21px → 9.4px
          // and "N removed from the project" resolved to 0px, i.e. invisible.
          // A row that grows for the rare four-line case beats a row that eats
          // its own text. See [[collection-row.spec]] "Row height".
          "flex min-h-24 w-full flex-row items-stretch",
          isInProgress && "pointer-events-none opacity-80",
        )}
      >
        {/* Only a settled report is worth opening: a running export has no
            versions to show yet, and a failed one has nothing at all. */}
        {isCompleted ? (
          <Link
            to="/projects/$projectId/reports/$reportId"
            params={{ projectId, reportId: collection.id }}
            className="flex min-w-0 flex-1 flex-row items-stretch"
          >
            {body}
          </Link>
        ) : (
          <div className="flex min-w-0 flex-1 flex-row items-stretch">
            {body}
          </div>
        )}
      </div>
    </Item>
  );
}

function CollectionThumbnail({
  latest,
  isInProgress,
  isFailed,
}: {
  latest: CollectionExport | null;
  isInProgress: boolean;
  isFailed: boolean;
}) {
  const thumbnailSrc = latest?.thumbnail ?? null;
  const isCompleted = !isInProgress && !isFailed;

  return (
    <div className="relative flex h-full w-20 shrink-0 items-center justify-center border-r bg-muted">
      {isInProgress && <FileText className="h-6 w-6 text-muted-foreground" />}
      {isFailed && <AlertCircle className="h-6 w-6 text-destructive" />}
      {isCompleted && thumbnailSrc && (
        <img
          src={thumbnailSrc}
          alt=""
          crossOrigin="anonymous"
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      {/* No cover — the report has never been exported, or the export produced
          none. A generic glyph, not a format-specific one: a format belongs to
          an export, and a report outlives any one of them. */}
      {isCompleted && !thumbnailSrc && (
        <FileText className="h-6 w-6 text-muted-foreground" />
      )}
    </div>
  );
}

function CollectionContent({
  collection,
  latest,
  isPhotos,
  count,
  isInProgress,
  isFailed,
  progress,
}: {
  collection: ReportCollection;
  latest: CollectionExport | null;
  isPhotos: boolean;
  count: number;
  isInProgress: boolean;
  isFailed: boolean;
  progress: ReturnType<typeof useReportProgress>;
}) {
  const countLabel = isPhotos
    ? `${count} ${count === 1 ? "photo" : "photos"}`
    : `${count} ${count === 1 ? "issue" : "issues"}`;

  // py-2, not prod's p-4: the content is justify-center, so vertical padding is
  // invisible whenever the content fits — it only sets the height at which the
  // row has to grow. 8px buys the removed-items line its natural line box
  // inside the same 96px row prod ships.
  return (
    <ItemContent className="flex min-w-0 flex-1 flex-col justify-center px-4 py-2">
      <ItemTitle className="w-full min-w-0">
        <span className="truncate">{collection.name}</span>
      </ItemTitle>
      {isInProgress && (
        <div className="mt-1 max-w-full">
          <ReportProgressBar progress={progress} />
        </div>
      )}
      {isFailed && (
        <ItemDescription className="text-destructive">
          {latest?.error_message || "Last export failed"}
        </ItemDescription>
      )}
      {!isInProgress && !isFailed && (
        <>
          {/* The kind's colour leads the line — blue issues, mint photos — so
              the two kinds stop reading as one stack of identical cards. The
              word after it says the same for anyone who can't see the colour.
              A div, not ItemDescription: its line-clamp sets a display that
              would fight the flex row. See [[collection-row.spec]]. */}
          <div className="flex min-w-0 items-center gap-1.5 text-sm leading-normal text-muted-foreground">
            {isPhotos ? (
              <ImageIcon
                data-slot="kind-icon"
                aria-hidden="true"
                className="size-4 shrink-0 text-kind-photos"
              />
            ) : (
              <ListChecks
                data-slot="kind-icon"
                aria-hidden="true"
                className="size-4 shrink-0 text-kind-issues"
              />
            )}
            <span className="truncate">
              {[countLabel, formatCollectionDate(latest?.created_at ?? null)]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </div>
          <ItemDescription>
            By {creatorName(collection.created_by)}
          </ItemDescription>
        </>
      )}
      {collection.deleted_item_count > 0 && (
        <span className="truncate text-xs text-muted-foreground">
          {collection.deleted_item_count} removed from the project
        </span>
      )}
    </ItemContent>
  );
}
